import {
	GetSiteLocationsDocument,
	type GetSiteLocationsQuery,
	type GetSiteLocationsQueryVariables,
	api as generatedApi
} from "~/services/graphql";

/**
 * Shared site-location RTK Query endpoint (used by `useStartingSites`, the sole path for FINISH,
 * MATERIAL, and RETAIL starting-node resolution). A second, paginated injection on top of the
 * codegen-generated `GetSiteLocations` (a plain single-page query): this `*Paginated` variant adds
 * merge/serializeQueryArgs so a user's granted sites accumulate across pages under one cache entry
 * regardless of how many starting-node grants they hold — wsapi caps `siteLocations` at
 * `maxAllowedItems: 50`, so a single `first: 200` fetch used to fail every request with HC0051 (P3-F1).
 */
const siteApi = generatedApi.injectEndpoints({
	endpoints: (build) => ({
		GetSiteLocationsPaginated: build.query<GetSiteLocationsQuery, GetSiteLocationsQueryVariables>({
			query: (variables) => ({ document: GetSiteLocationsDocument as unknown as string, variables }),
			// All pages for a given where-filter (the granted site-id set) share one cache entry.
			serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${JSON.stringify(queryArgs.where ?? {})}`,
			merge: (currentCache, newData) => {
				const current = currentCache.siteLocations;
				const incoming = newData.siteLocations;
				if (!current || !incoming) return;
				const existingIds = new Set((current.nodes ?? []).map((node) => node.id));
				const newNodes = (incoming.nodes ?? []).filter((node) => !existingIds.has(node.id));
				current.nodes = [...(current.nodes ?? []), ...newNodes];
				current.pageInfo = incoming.pageInfo;
				current.totalCount = incoming.totalCount;
			},
			forceRefetch: ({ currentArg, previousArg }) => JSON.stringify(currentArg) !== JSON.stringify(previousArg)
		})
	}),
	overrideExisting: false
});

export const { useGetSiteLocationsPaginatedQuery } = siteApi;

export default siteApi;
